/* Jogger theme JS — no dependencies. ~3 KB gzipped. */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.documentElement.classList.add('js');

  /* ---------------- Mobile-first runtime helpers (window.Jogger) ---------------- */
  const onReady = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn());
  const supportsIO = 'IntersectionObserver' in window;

  // 1) Lazy images: every <img loading="lazy"> fades in once decoded.
  const markLoaded = (img) => img.classList.add('is-loaded');
  // Product card photos that are already ~4:5 (within 6%) fill their 4:5
  // frame (.is-fill → object-fit: cover), so a coloured background reaches
  // the rounded corners; any other shape stays whole inside the frame.
  // Re-checked on every load, as the filter app swaps src after lazy-load.
  const fitCardImage = (img) => {
    if (!img.classList) return;
    const isCard = img.classList.contains('product-card__img');
    const isGallery = img.parentElement && (img.parentElement.classList.contains('pdp__media') || (img.parentElement.tagName === 'PICTURE' && img.parentElement.parentElement && img.parentElement.parentElement.classList.contains('pdp__media')));
    if (!isCard && !isGallery) return;
    const check = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const r = img.naturalWidth / img.naturalHeight;
      img.classList.toggle('is-fill', Math.abs(r - 0.8) / 0.8 <= 0.06);
    };
    check();
    if (!img.dataset.fitWatched) {
      img.dataset.fitWatched = '1';
      img.addEventListener('load', check);
    }
  };
  const prepImage = (img) => {
    fitCardImage(img);
    if (img.dataset.lazyPrepped) return;
    img.dataset.lazyPrepped = '1';
    if (img.getAttribute('loading') !== 'lazy' && !img.classList.contains('lazy-img')) return;
    img.classList.add('lazy-img');
    if (img.complete && img.naturalWidth > 0) return markLoaded(img);
    img.addEventListener('load', () => markLoaded(img), { once: true });
    img.addEventListener('error', () => markLoaded(img), { once: true });
    // Safety net: never leave an image invisible (cached decode, bfcache, etc.)
    setTimeout(() => markLoaded(img), 3500);
  };
  const prepImages = (root) => (root.querySelectorAll ? root.querySelectorAll('img') : []).forEach(prepImage);

  // 2) Reveal-on-scroll + lazy media (video/iframe) via one IntersectionObserver.
  const revealIO = supportsIO
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            el.classList.add('is-visible');
            if (el.matches('[data-reveal-stagger]')) [...el.children].forEach((c, i) => c.style.setProperty('--i', Math.min(i, 8)));
            if (el.matches('[data-lazy-video]')) loadLazyVideo(el);
            if (el.matches('[data-lazy-iframe]')) loadLazyIframe(el);
            revealIO.unobserve(el);
          });
        },
        { rootMargin: '0px 0px 200px 0px', threshold: 0.01 }
      )
    : null;
  const loadLazyVideo = (wrap) => {
    const video = wrap.tagName === 'VIDEO' ? wrap : wrap.querySelector('video');
    if (!video || video.dataset.lazyLoaded) return;
    video.dataset.lazyLoaded = '1';
    video.querySelectorAll('source[data-src]').forEach((s) => {
      s.src = s.dataset.src;
      s.removeAttribute('data-src');
    });
    if (video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
    }
    video.load();
    if (video.hasAttribute('autoplay') && !reducedMotion) {
      const p = video.play();
      if (p && p.catch) p.catch(() => {});
    }
  };
  const loadLazyIframe = (wrap) => {
    const frame = wrap.tagName === 'IFRAME' ? wrap : wrap.querySelector('iframe');
    if (!frame || !frame.dataset.src) return;
    frame.src = frame.dataset.src;
    frame.removeAttribute('data-src');
  };
  const observeLazy = (root) => {
    if (!root.querySelectorAll) return;
    const sel = '[data-reveal], [data-reveal-stagger], [data-lazy-video], [data-lazy-iframe]';
    const list = root.matches && root.matches(sel) ? [root, ...root.querySelectorAll(sel)] : [...root.querySelectorAll(sel)];
    list.forEach((el) => {
      if (el.dataset.lazyObserved) return;
      el.dataset.lazyObserved = '1';
      if (!revealIO || reducedMotion) {
        el.classList.add('is-visible');
        if (el.matches('[data-lazy-video]')) loadLazyVideo(el);
        if (el.matches('[data-lazy-iframe]')) loadLazyIframe(el);
        return;
      }
      revealIO.observe(el);
    });
  };

  // 3) Skeletons for content that arrives after a fetch (search, cart, filters…)
  const skeletonCard = () =>
    '<div class="skeleton-card" aria-hidden="true"><div class="skeleton skeleton--media"></div><div class="skeleton skeleton--text"></div><div class="skeleton skeleton--text is-short"></div></div>';
  const skeletonCards = (count = 4) => Array.from({ length: count }, skeletonCard).join('');
  const skeletonLines = (count = 3) =>
    '<div aria-hidden="true">' + Array.from({ length: count }, (_, i) => `<div class="skeleton skeleton--text${i === count - 1 ? ' is-short' : ''}"></div>`).join('') + '</div>';
  const showSkeleton = (container, html) => {
    if (!container) return;
    container.setAttribute('aria-busy', 'true');
    container.dataset.skeletonPrev = container.innerHTML;
    container.innerHTML = html;
  };
  const clearSkeleton = (container, html) => {
    if (!container) return;
    container.removeAttribute('aria-busy');
    delete container.dataset.skeletonPrev;
    if (typeof html === 'string') container.innerHTML = html;
    prepImages(container);
    observeLazy(container);
  };
  // Overlay variant: dims the existing grid instead of replacing it (used while filters reload)
  const setBusy = (container, busy) => {
    if (!container) return;
    container.classList.toggle('is-busy', !!busy);
    container.setAttribute('aria-busy', busy ? 'true' : 'false');
  };

  // 4) Layout variables: bottom dock height + visual viewport height (for drawers on iOS)
  const syncLayoutVars = () => {
    const dock = document.querySelector('.mobile-dock, [data-mobile-dock]');
    const dockVisible = dock && getComputedStyle(dock).display !== 'none' && getComputedStyle(dock).position === 'fixed';
    document.documentElement.style.setProperty('--dock-height', dockVisible ? dock.offsetHeight + 'px' : '0px');
    const vv = window.visualViewport;
    document.documentElement.style.setProperty('--vvh', (vv ? vv.height : window.innerHeight) + 'px');
  };
  let layoutRaf = null;
  const requestLayoutVars = () => {
    if (layoutRaf) return;
    layoutRaf = requestAnimationFrame(() => {
      syncLayoutVars();
      layoutRaf = null;
    });
  };
  window.addEventListener('resize', requestLayoutVars, { passive: true });
  window.addEventListener('orientationchange', requestLayoutVars, { passive: true });
  if (window.visualViewport) window.visualViewport.addEventListener('resize', requestLayoutVars, { passive: true });

  window.Jogger = Object.assign(window.Jogger || {}, {
    prepImages,
    observeLazy,
    skeletonCard,
    skeletonCards,
    skeletonLines,
    showSkeleton,
    clearSkeleton,
    setBusy,
    syncLayoutVars,
  });

  onReady(() => {
    prepImages(document);
    observeLazy(document);
    syncLayoutVars();
    // Theme editor: sections re-render without a page load
    document.addEventListener('shopify:section:load', (e) => {
      prepImages(e.target);
      observeLazy(e.target);
      syncLayoutVars();
    });
  });
  // Content injected later (filters, predictive search, cart drawer) gets the same treatment
  if ('MutationObserver' in window) {
    let lazyTimer = null;
    const pending = new Set();
    const lazyMO = new MutationObserver((mutations) => {
      for (const m of mutations) for (const n of m.addedNodes) if (n.nodeType === 1) pending.add(n);
      if (!pending.size) return;
      clearTimeout(lazyTimer);
      lazyTimer = setTimeout(() => {
        pending.forEach((n) => {
          if (!n.isConnected) return;
          if (n.tagName === 'IMG') prepImage(n);
          prepImages(n);
          observeLazy(n);
        });
        pending.clear();
      }, 40);
    });
    onReady(() => lazyMO.observe(document.body, { childList: true, subtree: true }));
  }

  /* ---------------- Sticky header: hide on scroll down, reveal on scroll up ---------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - this.lastY;
        this.classList.toggle('is-stuck', y > 8);
        // Never hide while the mobile drawer is open or near the very top.
        const drawerOpen = document.body.classList.contains('drawer-open');
        if (!drawerOpen) {
          if (delta > 6 && y > this.offsetHeight * 2) this.classList.add('is-hidden');
          else if (delta < -4 || y <= this.offsetHeight) this.classList.remove('is-hidden');
        }
        this.lastY = y;
        this.ticking = false;
      });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------------- Generic scroll-snap carousel with arrows + optional autoplay ---------------- */
  class JoggerCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel__track');
      if (!this.track) return;
      this.fade = this.dataset.transition === 'fade';
      this.index = 0;
      this.prevBtns = this.querySelectorAll('[data-carousel-prev]');
      this.nextBtns = this.querySelectorAll('[data-carousel-next]');
      this.prevBtns.forEach((b) => b.addEventListener('click', () => (this.fade ? this.goTo(this.index - 1) : this.scrollByPage(-1))));
      this.nextBtns.forEach((b) => b.addEventListener('click', () => (this.fade ? this.goTo(this.index + 1) : this.scrollByPage(1))));
      // Dots (hero) — collected before the first arrow/dot sync
      this.dots = this.querySelectorAll('[data-carousel-dot]');
      this.track.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
      window.addEventListener('resize', () => this.requestUpdate(), { passive: true });
      this.updateArrows();

      // Autoplay (hero)
      const interval = parseInt(this.dataset.autoplay || '0', 10);
      if (interval > 0 && !reducedMotion) {
        this.startAutoplay(interval);
        this.addEventListener('pointerenter', () => this.stopAutoplay());
        this.addEventListener('pointerleave', () => this.startAutoplay(interval));
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', () => this.startAutoplay(interval));
        document.addEventListener('visibilitychange', () => {
          document.hidden ? this.stopAutoplay() : this.startAutoplay(interval);
        });
      }

      this.dots.forEach((d, i) =>
        d.addEventListener('click', () => {
          if (this.fade) return this.goTo(i);
          const slide = this.track.children[i];
          if (slide) this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        })
      );

      if (this.fade) {
        // Gesture slide: pointer events cover touch, mouse drag and pen on all devices.
        // touch-action pan-y keeps vertical page scroll native while horizontal
        // swipes are delivered to us instead of being cancelled by the browser.
        this.track.style.touchAction = 'pan-y';
        let startX = 0;
        let lastX = 0;
        let tracking = false;
        let swiped = false;
        this.track.addEventListener('pointerdown', (e) => {
          startX = lastX = e.clientX;
          tracking = true;
          swiped = false;
        }, { passive: true });
        this.track.addEventListener('pointermove', (e) => {
          if (tracking) lastX = e.clientX;
        }, { passive: true });
        const finish = (endX) => {
          if (!tracking) return;
          tracking = false;
          const dx = endX - startX;
          if (Math.abs(dx) > 40) {
            swiped = true;
            this.goTo(this.index + (dx < 0 ? 1 : -1));
          }
        };
        this.track.addEventListener('pointerup', (e) => finish(e.clientX));
        // Some browsers cancel the pointer stream mid-gesture; use the last seen X
        this.track.addEventListener('pointercancel', () => finish(lastX));
        // Native image/link dragging steals the mouse gesture on desktop
        this.track.addEventListener('dragstart', (e) => e.preventDefault());
        // A swipe that ends on a link/button must not also trigger its click
        this.track.addEventListener('click', (e) => {
          if (swiped) {
            e.preventDefault();
            e.stopPropagation();
            swiped = false;
          }
        }, true);
        this.updateFade();
      }
    }
    goTo(i) {
      const slides = this.track.children;
      if (!slides.length) return;
      this.index = (i + slides.length) % slides.length;
      this.updateFade();
    }
    updateFade() {
      const slides = [...this.track.children];
      slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
      if (this.dots) this.dots.forEach((d, i) => d.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
      this.prevBtns.forEach((b) => b.removeAttribute('disabled'));
      this.nextBtns.forEach((b) => b.removeAttribute('disabled'));
    }
    startAutoplay(ms) {
      this.stopAutoplay();
      this.timer = setInterval(() => {
        if (this.fade) return this.goTo(this.index + 1);
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (this.track.scrollLeft >= max - 4) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.scrollByPage(1);
        }
      }, ms);
    }
    stopAutoplay() {
      if (this.timer) clearInterval(this.timer);
    }
    scrollByPage(dir) {
      const first = this.track.children[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const step = first ? first.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
      const perPage = Math.max(1, Math.floor(this.track.clientWidth / step));
      this.track.scrollBy({ left: dir * step * perPage, behavior: 'smooth' });
    }
    requestUpdate() {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.updateArrows();
        this.raf = null;
      });
    }
    updateArrows() {
      if (this.fade) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = Math.round(this.track.scrollLeft);
      this.prevBtns.forEach((b) => b.toggleAttribute('disabled', x <= 2));
      this.nextBtns.forEach((b) => b.toggleAttribute('disabled', x >= max - 2));
      if (this.dots && this.dots.length) {
        const slideW = this.track.children[0]?.getBoundingClientRect().width || 1;
        const idx = Math.round(x / slideW);
        this.dots.forEach((d, i) => d.setAttribute('aria-current', i === idx ? 'true' : 'false'));
      }
    }
  }
  customElements.define('jogger-carousel', JoggerCarousel);

  /* ---------------- Announcement bar rotator ---------------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.announcement__item')];
      if (this.items.length < 2 || reducedMotion) return;
      this.index = 0;
      const speed = Math.max(1000, parseInt(this.dataset.rotateSpeed || '4000', 10));
      this.timer = setInterval(() => {
        this.index = (this.index + 1) % this.items.length;
        this.items.forEach((el, i) => el.classList.toggle('is-active', i === this.index));
      }, speed);
      this.addEventListener('pointerenter', () => clearInterval(this.timer));
      this.addEventListener('pointerleave', () => {
        clearInterval(this.timer);
        this.timer = setInterval(() => {
          this.index = (this.index + 1) % this.items.length;
          this.items.forEach((el, i) => el.classList.toggle('is-active', i === this.index));
        }, speed);
      });
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  /* ---------------- Search overlay (predictive search) ---------------- */

  const RUPEE_SVG = '<svg viewBox="0 0 9 15" fill="currentColor" aria-hidden="true" style="height:0.82em;width:auto;display:inline-block;vertical-align:-0.02em;margin-right:0.12em"><path d="M3.72 14.28L5.96046e-08 7.78003V6.72003H0.66C1.3 6.72003 1.84667 6.65336 2.3 6.52003C2.75333 6.3867 3.11333 6.17336 3.38 5.88003C3.64667 5.57336 3.81333 5.18003 3.88 4.70003H5.96046e-08V3.36003H3.86C3.76667 2.9067 3.59333 2.53336 3.34 2.24003C3.08667 1.93336 2.74667 1.7067 2.32 1.56003C1.89333 1.41336 1.38 1.34003 0.78 1.34003H5.96046e-08V2.83718e-05H8.5V1.34003H4.9C5.18 1.59336 5.41333 1.8867 5.6 2.22003C5.78667 2.55336 5.90667 2.93336 5.96 3.36003H8.5V4.70003H6C5.90667 5.67336 5.53333 6.43336 4.88 6.98003C4.24 7.5267 3.4 7.88003 2.36 8.04003L6.22 14.28H3.72Z"/></svg>';
  // suggest.json gives prices as a decimal string in the presentment currency
  // ("1299.00"). A JSON number would be cents, as in the Ajax product API.
  const activeCurrency = () => (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
  const formatPrice = (amount) => {
    let n = typeof amount === 'number' ? amount / 100 : parseFloat(String(amount).replace(/[^0-9.\-]/g, ''));
    if (!isFinite(n)) return '';
    const cur = activeCurrency();
    try {
      if (cur === 'INR') {
        return RUPEE_SVG + new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
      }
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: cur }).format(n);
    } catch (e) {
      return n.toFixed(2);
    }
  };
  // Respect locale/market sub-folders (e.g. /en-us/) instead of hard-coding "/".
  const routeRoot = () => {
    const r = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return r.endsWith('/') ? r : r + '/';
  };

  const searchDrawer = () => document.getElementById('SearchDrawer');
  const openSearch = () => {
    const d = searchDrawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-open');
    setTimeout(() => d.querySelector('[data-search-input]')?.focus(), 60);
  };
  const closeSearch = () => {
    const d = searchDrawer();
    if (!d) return;
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-open');
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-open]')) {
      e.preventDefault();
      openSearch();
    }
    if (e.target.closest('[data-search-close]')) closeSearch();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.body.classList.contains('search-open')) closeSearch();
  });

  // Predictive results via Shopify's suggest endpoint
  let searchAbort = null;
  let searchDebounce = null;
  const money = (cents) => {
    const el = document.createElement('span');
    el.textContent = cents;
    return el.innerHTML;
  };
  const renderResults = (container, data, query) => {
    const products = data?.resources?.results?.products || [];
    const idle = searchDrawer()?.querySelector('[data-search-idle]');
    if (idle) idle.hidden = true;
    container.hidden = false;
    if (!products.length) {
      container.innerHTML = '<p class="search-drawer__empty">No products found for “' + money(query) + '”.</p>';
      return;
    }
    const items = products
      .map((p) => {
        const img = p.featured_image?.url
          ? '<img src="' + p.featured_image.url + (p.featured_image.url.includes('?') ? '&' : '?') + 'width=300" alt="" loading="lazy" width="150" height="150">'
          : '';
        return (
          '<a class="search-drawer__item" href="' + p.url + '">' + img +
          '<span class="search-drawer__item-title">' + money(p.title) + '</span>' +
          '<span class="search-drawer__item-price">' + (p.price ? formatPrice(p.price) : '') + '</span></a>'
        );
      })
      .join('');
    container.innerHTML =
      '<p class="search-drawer__results-title">Products</p>' +
      '<div class="search-drawer__grid">' + items + '</div>' +
      '<a class="search-drawer__all" href="' + routeRoot() + 'search?q=' + encodeURIComponent(query) + '&type=product">' +
      'View all results →</a>';
  };
  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-search-input]');
    if (!input) return;
    const container = searchDrawer()?.querySelector('[data-search-results]');
    if (!container) return;
    const q = input.value.trim();
    clearTimeout(searchDebounce);
    if (q.length < 2) {
      container.hidden = true;
      container.innerHTML = '';
      const idle = searchDrawer()?.querySelector('[data-search-idle]');
      if (idle) idle.hidden = false;
      return;
    }
    searchDebounce = setTimeout(async () => {
      try {
        if (searchAbort) searchAbort.abort();
        searchAbort = new AbortController();
        const url =
          routeRoot() + 'search/suggest.json?q=' + encodeURIComponent(q) +
          '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
        const res = await fetch(url, { signal: searchAbort.signal });
        if (!res.ok) return;
        const data = await res.json();
        renderResults(container, data, q);
      } catch (err) {
        if (err.name !== 'AbortError') console.warn('search', err);
      }
    }, 220);
  });

  /* ---------------- Mobile navigation drawer (multi-level slide) ---------------- */
  const MenuNav = {
    el: null,

    get drawer() {
      if (!this.el) this.el = document.getElementById('MobileDrawer');
      return this.el;
    },

    open() {
      const d = this.drawer;
      if (!d) return;
      d.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      // focus the first control once the slide-in has settled
      setTimeout(() => d.querySelector('button, a')?.focus(), 60);
    },

    close() {
      const d = this.drawer;
      if (!d) return;
      d.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      // reset to level 1 only after the close transition, so the user
      // never sees the sub-level snap back mid-fade
      setTimeout(() => this.reset(), 320);
      document.querySelector('[data-drawer-open]')?.focus();
    },

    reset() {
      const d = this.drawer;
      if (!d) return;
      d.classList.remove('is-sub-open');
      d.querySelectorAll('.drawer__subpanel.is-open').forEach((p) => {
        p.classList.remove('is-open');
        p.setAttribute('aria-hidden', 'true');
      });
      d.querySelectorAll('[data-submenu-open][aria-expanded="true"]').forEach((b) =>
        b.setAttribute('aria-expanded', 'false')
      );
    },

    openSub(trigger) {
      const d = this.drawer;
      const panel = document.getElementById(trigger.dataset.submenuOpen);
      if (!d || !panel) return;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      d.classList.add('is-sub-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.scrollTop = 0;
      setTimeout(() => panel.querySelector('[data-submenu-back]')?.focus(), 60);
    },

    closeSub(panel) {
      const d = this.drawer;
      if (!d || !panel) return;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      d.classList.remove('is-sub-open');
      const trigger = d.querySelector('[data-submenu-open][aria-expanded="true"]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        setTimeout(() => trigger.focus(), 60);
      }
    },

    // True when a second-level panel is showing
    isSubOpen() {
      return !!this.drawer && this.drawer.classList.contains('is-sub-open');
    },
  };

  document.addEventListener('click', (e) => {
    if (!document.getElementById('MobileDrawer')) return;

    if (e.target.closest('[data-drawer-open]')) {
      MenuNav.open();
      return;
    }
    if (e.target.closest('[data-drawer-close]')) {
      MenuNav.close();
      return;
    }
    const subOpen = e.target.closest('[data-submenu-open]');
    if (subOpen) {
      MenuNav.openSub(subOpen);
      return;
    }
    const subBack = e.target.closest('[data-submenu-back]');
    if (subBack) {
      MenuNav.closeSub(subBack.closest('.drawer__subpanel'));
    }
  });

  // Escape steps back one level, then closes — like a native app
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !document.body.classList.contains('drawer-open')) return;
    if (MenuNav.isSubOpen()) {
      MenuNav.closeSub(MenuNav.drawer.querySelector('.drawer__subpanel.is-open'));
    } else {
      MenuNav.close();
    }
  });

  /* ---------------- Wishlist (localStorage, cosmetic) ---------------- */
  const WKEY = 'jogger:wishlist';
  const getWishlist = () => {
    try {
      return JSON.parse(localStorage.getItem(WKEY)) || [];
    } catch {
      return [];
    }
  };
  const syncHearts = () => {
    const list = getWishlist();
    document.querySelectorAll('[data-wishlist]').forEach((btn) => {
      btn.setAttribute('aria-pressed', list.includes(btn.dataset.wishlist) ? 'true' : 'false');
    });
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.wishlist;
    let list = getWishlist();
    list = list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle];
    try {
      localStorage.setItem(WKEY, JSON.stringify(list));
    } catch {}
    syncHearts();
  });
  document.addEventListener('DOMContentLoaded', syncHearts);

  /* Filter apps (Globo) inject product cards after load, so their hearts start
     unfilled even for saved products. Re-sync whenever new cards appear. */
  if ('MutationObserver' in window) {
    let heartTimer = null;
    const heartObserver = new MutationObserver(function (mutations) {
      let sawCard = false;
      for (let i = 0; i < mutations.length; i++) {
        const added = mutations[i].addedNodes;
        for (let j = 0; j < added.length; j++) {
          const node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.querySelector && node.querySelector('[data-wishlist]')) {
            sawCard = true;
            break;
          }
          if (node.matches && node.matches('[data-wishlist]')) {
            sawCard = true;
            break;
          }
        }
        if (sawCard) break;
      }
      if (!sawCard) return;
      clearTimeout(heartTimer);
      heartTimer = setTimeout(syncHearts, 60);
    });
    const startHeartObserver = function () {
      if (document.body) heartObserver.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) startHeartObserver();
    else document.addEventListener('DOMContentLoaded', startHeartObserver);
  }

  /* ---------------- Footer accordions: open on desktop, collapsible on mobile ---------------- */
  const footerMedia = window.matchMedia('(min-width: 750px)');
  const syncFooterDetails = () => {
    document.querySelectorAll('details.site-footer__col').forEach((d) => {
      if (footerMedia.matches) d.setAttribute('open', '');
      else d.removeAttribute('open');
    });
  };
  footerMedia.addEventListener('change', syncFooterDetails);
  document.addEventListener('DOMContentLoaded', syncFooterDetails);

  /* ---------------- Inline video cards ---------------- */
  document.addEventListener('click', (e) => {
    const play = e.target.closest('[data-video-play]');
    if (!play) return;
    const card = play.closest('[data-video-card]');
    const video = card?.querySelector('video');
    if (!video) return;
    if (video.paused) {
      const p = video.play();
      if (p && p.catch) p.catch(() => card.classList.remove('is-playing'));
      card.classList.add('is-playing');
    } else {
      video.pause();
      card.classList.remove('is-playing');
    }
  });
})();
